import { useEffect, useState, useMemo } from 'react';

import {
  type Key,
  type ChatPermissions,
  KeyRequest,
  KeyStatusEnum,
  existingChatPermissions,
} from '@/core/chat/chat-keys/key';
import chatServiceApi from '@/core/chat/chat-keys/chat-service-api';
import { authService } from '@/core/auth/export';
import KeyFormView from '@/core/chat/chat-keys/key-form';
import KeyList from '@/core/chat//chat-keys/key-list';
import EmptyStateView from '@/core/chat//chat-keys/empty-state-view';
import KeyCreated from '@/core/chat/chat-keys/key-created';

enum ModalState {
  LIST,
  EMPTY,
  NO_PERMISSIONS,
  LOADING,
  CREATE,
  CREATED,
}

export default function ChatKeys() {
  const userChatPermissions = useMemo(() => {
    const raw =
      (authService
        .getCredentials()
        ?.body.permissions.filter(p => p.startsWith('read:chat-feed')) as ChatPermissions[]) ?? [];
    const allowed = new Set(existingChatPermissions.map(ep => ep.id));
    return raw.filter(p => allowed.has(p));
  }, []);

  const [keys, setKeys] = useState<Key[]>([]);
  const [newKeyPermissions, setNewKeyPermissions] =
    useState<ChatPermissions[]>(userChatPermissions);
  const [modalState, setModalState] = useState<ModalState>(ModalState.LOADING);
  const [copied, setCopied] = useState(false);
  const [generatedSecret, setGeneratedSecret] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const handleCancelCreate = () => {
    setCopied(false);
    setModalState(keys.length === 0 ? ModalState.EMPTY : ModalState.LIST);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(generatedSecret || '');
    setCopied(true);
  };

  const handleCreateKey = async () => {
    const request: KeyRequest = {
      name: newKeyName,
      permissions: newKeyPermissions,
    };

    const created = await chatServiceApi.createKey(request);

    setGeneratedSecret(created?.secret || null);

    setNewKeyName('');
    setNewKeyPermissions(userChatPermissions);
    loadKeys();
    setModalState(ModalState.CREATED);
  };

  useEffect(() => {
    async function fetchKeys() {
      try {
        if (userChatPermissions.length === 0) {
          setModalState(ModalState.NO_PERMISSIONS);
          return;
        }

        const result = await loadKeys();
        setModalState(result.content.length === 0 ? ModalState.EMPTY : ModalState.LIST);
      } catch (err) {
        console.error('Error fetching keys', err);
      }
    }

    fetchKeys();
  }, [userChatPermissions]);

  const loadKeys = async () => {
    const result = await chatServiceApi.userKeys({
      size: 100,
      filterOutStatus: KeyStatusEnum.REVOKED,
    });
    setKeys(result.content);
    return result;
  };

  if (modalState === ModalState.LOADING) {
    return <div>Loading...</div>;
  }

  if (modalState === ModalState.NO_PERMISSIONS) {
    return <div>You do not have permission to create chat keys. Please contact support.</div>;
  }

  if (modalState === ModalState.EMPTY) {
    return <EmptyStateView onCreateKey={() => setModalState(ModalState.CREATE)} />;
  }

  if (modalState === ModalState.LIST) {
    return (
      <KeyList
        keys={keys}
        loadKeys={loadKeys}
        createKey={() => setModalState(ModalState.CREATE)}
        userChatPermissions={userChatPermissions}
      />
    );
  }

  if (modalState === ModalState.CREATE) {
    return (
      <KeyFormView
        newKeyPermissions={newKeyPermissions}
        setNewKeyPermissions={setNewKeyPermissions}
        onSave={handleCreateKey}
        newKeyName={newKeyName}
        setNewKeyName={setNewKeyName}
        userChatPermissions={userChatPermissions}
        onCancel={handleCancelCreate}
      />
    );
  }

  if (modalState === ModalState.CREATED && generatedSecret) {
    return (
      <KeyCreated
        secretKey={generatedSecret}
        onCancel={handleCancelCreate}
        copied={copied}
        handleCopy={handleCopy}
      />
    );
  }
  return null;
}
